// Shared primitives, constants, and tiny presentational helpers used across
// the EuroPilot admin panel. Pulled out of the monolithic AmexImport.jsx
// during the Feb-2026 refactor so tab files stay small and self-contained.

// ---------------------------------------------------------------------------
// Constants shared by multiple tabs
// ---------------------------------------------------------------------------
export const METHODS = [
  { key: "visa",       label: "Visa",         color: "#1A1F71" },
  { key: "mastercard", label: "Mastercard",   color: "#EB001B" },
  { key: "amex",       label: "Amex",         color: "#1F72CD" },
  { key: "vipps",      label: "Vipps",        color: "#FF5B24" },
  { key: "applepay",   label: "Apple Pay",    color: "#27272A" },
  { key: "googlepay",  label: "Google Pay",   color: "#4285F4" },
  { key: "klarna",     label: "Klarna",       color: "#FFA8CD" },
];

// ---------------------------------------------------------------------------
// Card / Field / Stat — shared layout primitives
// ---------------------------------------------------------------------------
export function Card({ title, subtitle, accent, children, testid }) {
  return (
    <div className="border border-[var(--srs-line)] bg-[var(--srs-surface)] p-6 sm:p-8" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        <span className="w-1.5 h-1.5" style={{ background: accent }} />
        <span className="text-[10px] tracking-[0.25em] font-bold text-[var(--srs-ink-dim)]">{title}</span>
      </div>
      {subtitle && <p className="text-sm text-[var(--srs-ink-faint)] mb-6">{subtitle}</p>}
      {children}
    </div>
  );
}

export function Field({ label, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="block text-[10px] tracking-[0.25em] font-bold text-[var(--srs-ink-faint)] mb-2">{label}</span>
      {children}
    </label>
  );
}

export function Stat({ label, value, accent }) {
  return (
    <div className="bg-[var(--srs-bg)] p-5 sm:p-6">
      <div className="text-[10px] tracking-[0.25em] font-bold text-[var(--srs-ink-faint)] mb-3">{label}</div>
      <div className="font-display font-bold tracking-tight text-3xl sm:text-4xl" style={{ color: accent }}>{value}</div>
    </div>
  );
}

export function TokenWarning() {
  return null;
}

// ---------------------------------------------------------------------------
// Form input helpers (used by BroadcastTab, DealsTab, etc.)
// ---------------------------------------------------------------------------
export const Label = ({ children }) => <label className="block text-[10px] tracking-[0.2em] uppercase font-bold text-[var(--srs-ink-dim)] mb-1 mt-3">{children}</label>;
export const Input = (props) => <input {...props} className="w-full px-3 py-2 text-sm border border-[var(--srs-line)] bg-[var(--srs-bg)] text-[var(--srs-ink)] rounded-md focus:outline-none focus:border-[#FF8A5C]" />;
export const Textarea = (props) => <textarea {...props} className="w-full px-3 py-2 text-sm border border-[var(--srs-line)] bg-[var(--srs-bg)] text-[var(--srs-ink)] rounded-md focus:outline-none focus:border-[#FF8A5C] resize-y" />;
export const Select = (props) => <select {...props} className="w-full px-3 py-2 text-sm border border-[var(--srs-line)] bg-[var(--srs-bg)] text-[var(--srs-ink)] rounded-md focus:outline-none focus:border-[#FF8A5C]" />;

export function KPI({ label, value, accent, testid, big }) {
  return (
    <div className="border border-[var(--srs-line)] bg-[var(--srs-surface)] p-3 rounded-lg" data-testid={testid}>
      <div className="text-[9px] tracking-[0.25em] font-bold uppercase text-[var(--srs-ink-dim)] mb-2">{label}</div>
      <div className={`font-bold ${big ? "text-2xl" : "text-xl"}`} style={{ color: accent || "var(--srs-ink)" }}>{value}</div>
    </div>
  );
}

export function Row({ label, value, bold, accent }) {
  return (
    <div className="flex justify-between items-baseline py-1 text-sm">
      <span className="text-[var(--srs-ink-dim)]">{label}</span>
      <span className={bold ? "font-bold" : ""} style={accent ? { color: accent } : { color: "var(--srs-ink)" }}>{value}</span>
    </div>
  );
}

export function StatBlock({ label, value, accent }) {
  return (
    <div className="border border-[var(--srs-line)] bg-[var(--srs-surface)] rounded-lg p-5">
      <div className="text-[10px] tracking-[0.25em] font-bold text-[var(--srs-ink-faint)]" style={{ color: accent }}>{label}</div>
      <div className="mt-2 text-3xl font-bold text-[var(--srs-ink)] tabular-nums">{value}</div>
    </div>
  );
}
